<template>
  <div ref="modal" class="dtf-first-sticker-promo-modal modal" role="dialog">
    <div class="dtf-first-sticker-promo-modal__modal-box modal-dialog">
      <div class="dtf-first-sticker-promo-modal__modal-body">
        <button
          class="dtf-first-sticker-promo-modal__modal-button-close"
          type="button"
          @click="closeModal"
        >
          <UseSvg icon="modal-close-big" />
        </button>
        <div class="dtf-first-sticker-promo-modal__feature-image">
          <img
            src="https://jiffy.imgix.net/gen/jiffy-first-sticker-promo-modal__banner.png?auto=format&w=400&h=400&q=80&dpr=2"
            alt="Jiffy Stickers promotion"
            class="dtf-first-sticker-promo-modal__banner"
          />
        </div>
        <div class="dtf-first-sticker-promo-modal__alert">
          <img
            src="https://jiffy.imgix.net/gen/jiffy-first-sticker-promo-modal__logo.png?auto=format&w=400&h=400&q=80&dpr=2"
            alt="Jiffy Stickers"
            class="dtf-first-sticker-promo-modal__logo"
          />
          <div class="dtf-first-sticker-promo-modal__promo-text">
            <div class="dtf-first-sticker-promo-modal__promo-text--large">30% OFF</div>
            <div class="dtf-first-sticker-promo-modal__promo-text--small">
              YOUR FIRST JIFFY STICKERS ORDER!
            </div>
          </div>
          <div class="dtf-first-sticker-promo-modal__promo-code">
            Code:
            <span
              id="js-promo-code"
              class="dtf-first-sticker-promo-modal__promo-code--bold"
              @click="copyPromoCode"
            >
              STICKERS30
            </span>
          </div>
          <div class="dtf-first-sticker-promo-modal__text">Use this code at checkout</div>
          <img
            src="https://jiffy.imgix.net/gen/jiffy-first-sticker-promo-modal__delivery.png?auto=format&w=400&h=400&q=80&dpr=2"
            alt="DELIVERED AS SOON AS TOMORROW"
            class="dtf-first-sticker-promo-modal__delivery"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { track } from "jiffy-analytics";
import { api } from "jiffy-api";
import { writeCookie } from "common/utils/cookie";
import { track as rudderstackTrack } from "common/utils/rudderstack/tracker";
import { asyncRetryIfGatewayError } from "common/utils/retries";
import { mapState } from "pinia";
import UseSvg from "../vue-cart/UseSvg.vue";
import { useDtfStore } from "./store";

export default {
  name: "DtfFirstStickerPromoModal",
  components: { UseSvg },
  emits: ["submit", "close"],
  computed: {
    ...mapState(useDtfStore, ["showFirstStickerPromoModal"])
  },
  data() {
    return {
      closed: false
    };
  },
  mounted() {
    this.tryShowingPromoModal();
  },
  methods: {
    async copyPromoCode() {
      const promoCode = "STICKERS30";
      try {
        await navigator.clipboard.writeText(promoCode);
      } catch {
        // Handle clipboard error (optional)
      }
    },
    async tryShowingPromoModal() {
      if (this.showFirstStickerPromoModal) {
        this.showModal();
        return;
      }

      try {
        await asyncRetryIfGatewayError(async () => {
          const { show: eligibleForPromo } = await api.dtf.firstStickerPromoModal();
          if (eligibleForPromo) this.showModal();
        });
      } catch (error) {
        window.Honeybadger?.notify("DTF first sticker promo modal error", {
          params: { error: this.parseError(error) }
        });
      }
    },
    parseError(error) {
      return Object.keys(error).length === 0 ? error.message : error;
    },
    markModalAsShown() {
      writeCookie("dtf-first-sticker-promo-shown", true, 86400);
    },
    showModal() {
      track("dtf_first_sticker_promo_modal_shown");
      rudderstackTrack("Promotion Viewed", {
        is_app: Boolean(window.MOBILE_APP),
        location: "dtf_first_sticker_promo_modal",
        platform: window.MOBILE_APP_PLATFORM || "web",
        text: "30% OFF YOUR FIRST JIFFY STICKERS ORDER!",
        type: "promotion_modal",
        url: window.location.href
      });
      this.markModalAsShown();
      const $$modal = $(this.$refs.modal);
      $$modal.modal("show").on("hidden.bs.modal", () => {
        $$modal.off("hidden.bs.modal");
        if (!this.closed) this.closeModal();
      });
    },
    closeModal() {
      track("dtf_first_sticker_promo_modal_closed");
      $(this.$refs.modal).modal("hide");
      this.closed = true;
      this.$emit("close");
    }
  }
};
</script>

<style scoped lang="scss">
@import url("https://fonts.googleapis.com/css2?family=Poppins:wght@400;700;900&display=swap");
@import "frontend/init/jiffy/mixin.scss";
@import "frontend/init/jiffy/settings.scss";
@import "frontend/init/jiffy/button/mixin.scss";

$promo-primary-font: "Poppins", sans-serif;
$promo-secondary-font: "Arial Black", Arial, sans-serif;
$promo-blue-dark: #1f1d55;

.dtf-first-sticker-promo-modal {
  font-family: $promo-primary-font;

  &__modal-box {
    margin: auto;
    position: absolute;
    inset: 0;
    width: 360px;

    @include with-tablet {
      width: 768px;
      height: 580px;
    }
  }

  &__modal-body {
    display: flex;
    flex-direction: column;
    height: 100%;

    @include with-tablet {
      flex-direction: row;
      height: auto;
    }
  }

  &__modal-button-close {
    @include clear-button;
    @include svg-wrapper;

    position: absolute;
    top: 0;
    right: 0;
    width: 48px;
    height: 48px;
    padding: 16px;
    z-index: 1;
  }

  &__feature-image {
    flex: 0 0 45%;
    flex-basis: 256px;
    position: relative;
    overflow: hidden;
    min-height: 400px;
    background-color: $crafterpay-gray-modern-100;
    border-top-left-radius: 3px;
    border-top-right-radius: 3px;

    @include with-tablet {
      flex: 1 1 50%;
      height: 580px;
      border-top-right-radius: 3px;
      border-bottom-left-radius: 3px;
    }
  }

  &__banner {
    width: 100%;
    height: 100%;
    object-fit: fill;
  }

  &__delivery {
    width: 295px;
    margin-top: 8px;
  }

  &__alert {
    flex: 0 0 50%;
    background-color: $promo-blue-light;
    color: $white;
    display: flex;
    flex-direction: column;
    align-items: center;
    padding-top: 40px;
    border-bottom-left-radius: 3px;
    border-bottom-right-radius: 3px;

    @include with-tablet {
      flex-basis: 55%;
      height: 580px;
      border-bottom-left-radius: 0;
      border-top-right-radius: 3px;
      padding-top: 110px;
      padding-bottom: 20px;
    }
  }

  &__logo {
    width: 280px;
  }

  &__promo-text {
    font-weight: 700;
    color: $white;
    text-align: center;
    line-height: 48px;
    margin-top: 30px;

    &--small {
      font-size: 16px;
      font-weight: 900;
    }

    &--large {
      font-size: 64px;
    }
  }

  &__promo-code {
    border-radius: 3px;
    border: 1.5px dashed $promo-blue-dark;
    color: $promo-blue-dark;
    cursor: pointer;
    background: #f1ffff;
    padding: 10px 15px;
    font-size: 24px;
    line-height: 100%;
    margin: 0 auto 10px;

    &--bold {
      font-weight: 700;
      font-family: $promo-secondary-font;
    }
  }

  &__text {
    font-size: 15px;
    line-height: 20px;
  }
}
</style>
